import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import DiscoveryModal from '../components/DiscoveryModal/DiscoveryModal';
import WelcomeModal from '../components/WelcomeModal/WelcomeModal';
import { useHints } from './HintContext';
import { HINTS, HINT_MAP } from './hints.config';
import '../components/HintGlow/HintGlow.css';

/**
 * HintLayer — mounted once in App. Handles:
 * 1. DOM glow application (MutationObserver for dynamic elements)
 * 2. Capture-phase click interception for DOM hints
 * 3. Page-enter auto-discovery
 * 4. Discovery modal rendering
 */
export default function HintLayer() {
  const location = useLocation();
  const {
    isHintPending,
    triggerDiscovery,
    confirmDiscovery,
    dismissDiscovery,
    activeDiscoveryId,
    hintsActive,
    seenHints,
    showIntake,
    showWelcome,
    dismissWelcome,
    intakeExamInterests,
    postIntakeQueue,
    markHintAsSeen,
    hasParsedDeck: hasParsedDeckState,
  } = useHints();

  const hasInterest = (hint) => {
    if (!hint.requiresInterest) return true;
    const interests = Array.isArray(intakeExamInterests) ? intakeExamInterests : [];
    return interests.includes(hint.requiresInterest);
  };

  // Runtime gate: hints requiring an uploaded+parsed deck. Backed by
  // HintContext state set by Dashboard via setHasParsedDeck on every
  // decks-list refresh — context state means applyHints reruns when
  // the flag flips.
  const hasParsedDeck = (hint) => {
    if (!hint.requiresParsedDeck) return true;
    return Boolean(hasParsedDeckState);
  };

  const postIntakeHead = Array.isArray(postIntakeQueue) ? postIntakeQueue[0] || null : null;
  const isQueueGated = (hint) => !hint.postIntakeOnly || postIntakeHead === hint.id;

  // Keep current state in ref so DOM event handlers always read fresh values
  const stateRef = useRef({ isHintPending, triggerDiscovery, markHintAsSeen });
  useEffect(() => {
    stateRef.current = { isHintPending, triggerDiscovery, markHintAsSeen };
  });

  // ── DOM glow + click interception ──────────────────────────────
  useEffect(() => {
    if (!hintsActive) return;

    const cleanups = [];
    let debounceTimer = null;

    function applyHints() {
      // Clear previous
      cleanups.forEach(fn => fn());
      cleanups.length = 0;

      const domHints = HINTS.filter(h => h.type === 'dom');

      // Single-active-hint discipline: glow ONE hint at a time, not all
      // pending ones. Priority order is HINTS array order (encodes the
      // onboarding flow). If the post-intake queue has a head, that wins
      // and short-circuits the search. Multi-element hints (`glowAll`)
      // still glow all their elements — the gate is "one hint at a time,"
      // not "one element at a time."
      let chosenHint = null;
      if (postIntakeHead) {
        const queued = HINT_MAP[postIntakeHead];
        if (queued && queued.type === 'dom') {
          chosenHint = queued;
        }
      }
      if (!chosenHint) {
        for (const hint of domHints) {
          if (!stateRef.current.isHintPending(hint.id)) continue;
          if (!hasInterest(hint)) continue;
          if (!hasParsedDeck(hint)) continue;
          if (!isQueueGated(hint)) continue;

          // Page match (null = any page, string or array of prefixes)
          if (hint.page) {
            const pages = Array.isArray(hint.page) ? hint.page : [hint.page];
            if (!pages.some(p => location.pathname.startsWith(p))) continue;
          }

          // Selector must match at least one element on the page; otherwise
          // this hint is a no-op right now and we should fall through to
          // the next candidate instead of letting it silently win the slot.
          if (document.querySelectorAll(hint.selector).length === 0) continue;

          chosenHint = hint;
          break;
        }
      }

      if (chosenHint) {
        const hint = chosenHint;
        // Re-run gates / page check for the queue-head path (the loop
        // above already checked them, but the queue-head bypass didn't).
        const pageOk = !hint.page
          || (Array.isArray(hint.page) ? hint.page : [hint.page]).some(p => location.pathname.startsWith(p));
        const eligible = pageOk
          && stateRef.current.isHintPending(hint.id)
          && hasInterest(hint)
          && hasParsedDeck(hint)
          && isQueueGated(hint);
        if (!eligible) return;

        const allEls = document.querySelectorAll(hint.selector);
        if (allEls.length === 0) return;

        let targets;
        if (hint.targetIndex === 'middle' && allEls.length > 1) {
          targets = [allEls[Math.floor(allEls.length / 2)]];
        } else if (hint.glowAll) {
          targets = Array.from(allEls);
        } else {
          targets = [allEls[0]];
        }

        const glowClasses = [hint.glowClass || 'hint-glow'];
        if (hint.category) {
          glowClasses.push(`hint-glow--${hint.category}`);
        }

        for (const el of targets) {
          glowClasses.forEach(cls => el.classList.add(cls));
          cleanups.push(() => glowClasses.forEach(cls => el.classList.remove(cls)));

          if (hint.interceptClick) {
            const handler = (e) => {
              if (stateRef.current.isHintPending(hint.id)) {
                e.stopPropagation();
                e.preventDefault();
                stateRef.current.triggerDiscovery(hint.id);
              }
            };
            el.addEventListener('click', handler, true); // capture phase
            cleanups.push(() => el.removeEventListener('click', handler, true));
          }
          if (hint.revealAfterAction) {
            // Let the element's normal click action fire (route change,
            // tab open, whatever). Then schedule the discovery modal so
            // it surfaces ON TOP of the destination view, not in place
            // of it. 700ms covers most route transitions; the modal
            // re-checks isHintPending so a redundant click doesn't
            // double-fire.
            const handler = () => {
              if (!stateRef.current.isHintPending(hint.id)) return;
              setTimeout(() => {
                if (stateRef.current.isHintPending(hint.id)) {
                  stateRef.current.triggerDiscovery(hint.id);
                }
              }, 700);
            };
            el.addEventListener('click', handler, true); // capture, no preventDefault
            cleanups.push(() => el.removeEventListener('click', handler, true));
          }
          if (hint.markSeenOnClick) {
            // Click flows through normally. We just stamp the hint seen
            // so the glow + any toast for this hint stop on first
            // selection. No modal — the click action IS the discovery.
            const handler = () => {
              if (!stateRef.current.isHintPending(hint.id)) return;
              stateRef.current.markHintAsSeen(hint.id);
            };
            el.addEventListener('click', handler, true);
            cleanups.push(() => el.removeEventListener('click', handler, true));
          }
        }
      }
    }

    applyHints();

    // MutationObserver — re-apply when DOM changes (debounced)
    const observer = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(applyHints, 80);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(debounceTimer);
      cleanups.forEach(fn => fn());
      observer.disconnect();
    };
  }, [hintsActive, seenHints, location.pathname, postIntakeHead, hasParsedDeckState]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Page-enter hints ───────────────────────────────────────────
  useEffect(() => {
    if (!hintsActive) return;
    const pageEnterHints = HINTS.filter(h => h.type === 'page-enter');
    for (const hint of pageEnterHints) {
      if (!hasInterest(hint)) continue;
      if (!hasParsedDeck(hint)) continue;
      const pageMatch = hint.page
        ? (Array.isArray(hint.page) ? hint.page : [hint.page]).some(p => location.pathname.startsWith(p))
        : true;
      if (pageMatch && isHintPending(hint.id)) {
        triggerDiscovery(hint.id);
        break; // one at a time
      }
    }
  }, [location.pathname, hintsActive, isHintPending, triggerDiscovery, intakeExamInterests]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Toast for queue-head hints carrying a `toast` field ────────
  // Surfaces alongside the DOM glow (no modal) so the user gets a
  // bottom-of-screen instruction without losing the loaded view.
  // Auto-dismisses when the hint is marked seen (e.g. user clicks
  // any glowed target).
  const queueHeadHint = postIntakeHead ? HINT_MAP[postIntakeHead] : null;
  const showQueueToast = (
    hintsActive
    && !showIntake
    && !showWelcome
    && !activeDiscoveryId
    && queueHeadHint?.toast
    && isQueueGated(queueHeadHint)
    && hasInterest(queueHeadHint)
    && hasParsedDeck(queueHeadHint)
    && (!queueHeadHint.page || (Array.isArray(queueHeadHint.page) ? queueHeadHint.page : [queueHeadHint.page]).some(p => location.pathname.startsWith(p)))
  );

  // ── Discovery modal ────────────────────────────────────────────
  // Intake / DLC selection is no longer an auto-modal — it used to overlap the
  // first-login keystone gate. The dashboard surfaces a "choose free DLC" pill
  // (DlcTray) that opens IntakeModal on demand instead.

  if (showWelcome) {
    return <WelcomeModal onDismiss={dismissWelcome} />;
  }

  if (!activeDiscoveryId) {
    if (showQueueToast) {
      return <HintToast title={queueHeadHint.title} body={queueHeadHint.toast} />;
    }
    return null;
  }

  return (
    <DiscoveryModal
      hintId={activeDiscoveryId}
      onConfirm={async () => {
        const hintId = activeDiscoveryId;
        await confirmDiscovery();
        const hint = HINT_MAP[hintId];
        if (hint?.postAction) {
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent(`tutorial:${hint.postAction}`));
          }, 300);
        }
      }}
      onClose={dismissDiscovery}
    />
  );
}

// Bottom-center instruction toast. Used for post-intake hints whose
// click action IS the discovery (e.g. area-pick) — there's no modal,
// just a glow + this toast telling the user what tapping does.
// Auto-dismisses when the queue advances past the hint (HintLayer
// stops rendering the toast on next render).
function HintToast({ title, body }) {
  // Anchor to the dashboard map viewport (the <main>), not the entire
  // window — otherwise the toast renders left-of-center because the
  // ToC sidebar pushes the map content right. ResizeObserver tracks
  // sidebar collapse / window resize so the toast follows the actual
  // map center. Vertically: just above the MapOrbTray (~270px from
  // viewport bottom).
  const [centerX, setCenterX] = React.useState(null);
  React.useEffect(() => {
    const measure = () => {
      const el = document.querySelector('main.page-shell-main, main.dashboard-content');
      if (!el) return;
      const r = el.getBoundingClientRect();
      setCenterX(r.left + r.width / 2);
    };
    measure();
    window.addEventListener('resize', measure);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      const el = document.querySelector('main.page-shell-main, main.dashboard-content');
      if (el) {
        ro = new ResizeObserver(measure);
        ro.observe(el);
      }
    }
    return () => {
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, []);
  const leftPx = centerX != null ? `${centerX}px` : '50%';
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 'var(--hint-toast-bottom, 270px)',
        left: leftPx,
        transform: 'translateX(-50%)',
        zIndex: 500,
        maxWidth: '520px',
        padding: '14px 22px 14px 18px',
        background: 'rgba(15,15,17,0.94)',
        border: '1px solid rgba(168,85,247,0.55)',
        borderLeft: '3px solid #a855f7',
        borderRadius: '10px',
        boxShadow: '0 14px 36px rgba(0,0,0,0.55), 0 0 26px rgba(168,85,247,0.28)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        color: '#fff',
        fontFamily: 'inherit',
        pointerEvents: 'none',
      }}
    >
      <div style={{
        fontSize: '11px',
        fontWeight: 800,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: '#c4b5fd',
        marginBottom: '4px',
      }}>{title}</div>
      <div style={{
        fontSize: '13.5px',
        fontWeight: 500,
        lineHeight: 1.45,
        color: 'rgba(255,255,255,0.92)',
      }}>{body}</div>
    </div>
  );
}
